import { useState, useEffect, useCallback, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { message } from 'antd';
import { campaignManagerAPI } from './api';
import {
  mapListItemsToCatalogOpportunities,
  toApiOpportunityFilterStatuses,
  toApiOpportunityStatus,
  toApiDealAssetStatus,
  mapFrontendTypeToApi,
  mapApiDetailToOpportunity,
} from './mapper';
import { hydrateAdSalesFormData } from './adSalesHydrate';
import { fromIsoDatetime } from './wizardMappers';
import { stripServerOwnedPaymentFields } from './paymentEcho';
import {
  ApiBrand,
  ApiPackageCatalogItem,
  ApiPaymentDto,
  CampaignListParams,
  WizardInitInfo,
  CampaignSelectionItem,
  CampaignSelectionParams,
  FileUploadResult,
  DraftCampaignType,
  DuplicateCampaignData,
  BaselineV2Response, ApiCampaignType } from './types';
import { MOCK_OPPORTUNITIES } from '../../pages/CampaignCatalog/mockData';
import { Opportunity as CatalogOpportunity, OpportunityStatus } from '../../pages/CampaignCatalog/types';
import { Opportunity as DetailOpportunity } from '../../pages/CampaignDetail/opportunity';
import { errorMessage } from './apiError';

/** Feature flag — flip to true when backend API is ready */
const USE_REAL_API = true;

// ─── Utility: Debounce ──────────────────────────────────────

/** Debounce a value by `delay` ms. Useful for search inputs to avoid API calls per keystroke. */
export function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Brands hook ────────────────────────────────────────────

interface UseBrandsResult {
  data: ApiBrand[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetch account-scoped brand list (Q21). Used for brand filter + brand selectors.
 *  `enabled: false` keeps the hook inert (empty data, no request) for callers
 *  that render the same UI off local data — e.g. the guided-tour catalog.
 *  `view: 'basic'` asks for identity fields only: the API skips the per-brand network and affiliate lookups
 *  (3-4s for a page of brands on staging), so filters and market lookups should always pass it. Wizards that
 *  read `inSupportNetwork` / `affiliateLink` keep the default full view. */
export function useBrands(params?: { q?: string; enabled?: boolean; view?: 'basic' | 'full' }): UseBrandsResult {
  const [data, setData] = useState<ApiBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  const enabled = params?.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      setData([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      if (USE_REAL_API) {
        try {
          const response = await campaignManagerAPI.getBrands({
            ...(params?.q && { q: params.q }),
            ...(params?.view && { view: params.view }),
          });
          if (!cancelled) {
            setData(response.data);
          }
        } catch {
          if (!cancelled) {
            setError('Failed to load brands');
            message.error('Failed to load brands');
          }
        }
      } else {
        // Mock path — derive from MOCK_MERCHANT brands
        const { MOCK_MERCHANT } = await import('../../pages/CreateUpsize/mockData');
        if (!cancelled) {
          setData(
            MOCK_MERCHANT.brands.map((b) => ({
              id: b.id,
              name: b.name,
              country: 'SG',
              legacyMerchantId: null,
              branchLink: null,
              integrationType: null,
              networkName: null,
              logoDarkUrl: null,
              logoLightUrl: null,
              inSupportNetwork: false,
              affiliateLink: null,
            }))
          );
        }
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [params?.q, params?.view, enabled, refreshKey]);

  return { data, loading, error, refetch };
}

// ─── Packages hook ──────────────────────────────────────────

interface UsePackagesResult {
  data: ApiPackageCatalogItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch package catalog (Q20). Used for subtype filter + package pickers.
 *
 * Pass merchantId on the self-service create flows: it switches the API to the eligibility-filtered
 * catalog whose floors are resolved for that merchant's category. Callers must treat an empty `data`
 * with no `error` as "nothing bookable", never as "not loaded" — the two are different answers.
 */
export function usePackages(params?: { countries?: string; types?: string; merchantId?: string }): UsePackagesResult {
  const [data, setData] = useState<ApiPackageCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    // Skip fetch when no params provided (no brand selected yet)
    if (!params) {
      setData([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      if (USE_REAL_API) {
        try {
          const response = await campaignManagerAPI.getPackages(params);
          if (!cancelled) {
            setData(response.data);
          }
        } catch {
          if (!cancelled) {
            setError('Failed to load packages');
            message.error('Failed to load packages');
          }
        }
      } else {
        // Mock path — static subtype options
        if (!cancelled) {
          setData([
            {
              id: '1',
              country: 'SG',
              key: 'platinum',
              order: 1,
              name: 'Platinum',
              type: 'UPSIZE',
              assets: {},
              createSelectable: true,
              status: 'ACTIVE',
              extra: null,
            },
            {
              id: '2',
              country: 'SG',
              key: 'gold',
              order: 2,
              name: 'Gold',
              type: 'UPSIZE',
              assets: {},
              createSelectable: true,
              status: 'ACTIVE',
              extra: null,
            },
            {
              id: '3',
              country: 'SG',
              key: 'silver',
              order: 3,
              name: 'Silver',
              type: 'UPSIZE',
              assets: {},
              createSelectable: true,
              status: 'ACTIVE',
              extra: null,
            },
            {
              id: '4',
              country: 'SG',
              key: 'category-domination',
              order: 1,
              name: 'Category Domination',
              type: 'AD_SALES',
              assets: {},
              createSelectable: true,
              status: 'ACTIVE',
              extra: null,
            },
            {
              id: '5',
              country: 'SG',
              key: 'platinum-booster',
              order: 2,
              name: 'Platinum Booster',
              type: 'AD_SALES',
              assets: {},
              createSelectable: true,
              status: 'ACTIVE',
              extra: null,
            },
          ]);
        }
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [params?.countries, params?.types, params?.merchantId, refreshKey]);

  return { data, loading, error, refetch };
}

// ─── Package assets hook ─────────────────────────────────────

interface UsePackageAssetsResult {
  data: import('./types').ApiPackageAssetItem[];
  loading: boolean;
  error: string | null;
}

/** Fetch asset details for a list of asset IDs (keys from ApiPackageCatalogItem.assets). */
export function usePackageAssets(assetIds: string[]): UsePackageAssetsResult {
  const [data, setData] = useState<import('./types').ApiPackageAssetItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = assetIds.join(',');

  useEffect(() => {
    if (!assetIds.length) {
      setData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    campaignManagerAPI
      .getPackageAssets(assetIds)
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load package assets');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, loading, error };
}

// ─── List hook ───────────────────────────────────────────────

interface UseCampaignListResult {
  data: CatalogOpportunity[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCampaignList(params: {
  enabled?: boolean;
  search?: string;
  type?: string | null;
  status?: string;
  brand?: string | null;
  subtype?: string | null;
  subtypeCountry?: string | null;
  offset?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  createdBys?: string[];
  /** Creation sources to include, e.g. ['MERCHANT'] for merchant self-serve only. */
  creationSources?: string[];
  sort?: string;
}): UseCampaignListResult {
  const [data, setData] = useState<CatalogOpportunity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    if (params.enabled === false) {
      setData([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      if (USE_REAL_API) {
        try {
          const apiParams: CampaignListParams = {
            offset: params.offset || 0,
            limit: params.limit || 25,
          };
          if (params.search) apiParams.q = params.search;
          if (params.type && ['AD_SALES', 'UPSIZE', 'STANDALONE_OFFER'].includes(params.type)) {
            apiParams.types = [mapFrontendTypeToApi(params.type as 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER')];
          }
          if (params.status && params.status !== 'ALL') {
            // Convert frontend display status to API status before sending
            apiParams.statuses = toApiOpportunityFilterStatuses(params.status as OpportunityStatus);
          }
          if (params.brand) apiParams.merchantIds = [params.brand];
          if (params.subtype) {
            apiParams.packageTiers = [Number(params.subtype)];
            // Package key is scoped per country — must pass both for unambiguous filter
            if (params.subtypeCountry) apiParams.countries = [params.subtypeCountry];
          }
          if (params.startDate) apiParams.startDate = params.startDate;
          if (params.endDate) apiParams.endDate = params.endDate;
          if (params.createdBys?.length) apiParams.createdBys = params.createdBys;
          if (params.creationSources?.length) apiParams.creationSources = params.creationSources;
          if (params.sort) apiParams.sort = params.sort;

          const response = await campaignManagerAPI.getItems(apiParams);
          if (!cancelled) {
            setData(mapListItemsToCatalogOpportunities(response.data));
            setTotal(response.total);
          }
        } catch (err) {
          if (!cancelled) {
            setError('Failed to load campaigns');
            message.error('Failed to load campaigns');
          }
        }
      } else {
        // Mock path — client-side filter + sort + paginate
        let result = [...MOCK_OPPORTUNITIES];
        if (params.search) {
          const q = params.search.toLowerCase();
          result = result.filter((o) => o.numericId.includes(q) || o.brand.toLowerCase().includes(q));
        }
        if (params.type) result = result.filter((o) => o.type === params.type);
        if (params.status && params.status !== 'ALL') result = result.filter((o) => o.status === params.status);
        if (params.brand) result = result.filter((o) => o.brand === params.brand);
        if (params.startDate) {
          result = result.filter((o) => o.startDate && o.startDate >= params.startDate!);
        }
        if (params.endDate) {
          result = result.filter((o) => o.endDate && o.endDate <= params.endDate!);
        }

        const [sortField = 'startDate', sortDirection = 'asc'] = (params.sort || 'startDate:asc').split(':');
        const direction = sortDirection === 'desc' ? -1 : 1;
        result.sort((a, b) => {
          if (sortField === 'merchant.name') {
            return a.brand.localeCompare(b.brand, undefined, { sensitivity: 'base' }) * direction;
          }
          const aTime = a.startDate ? new Date(a.startDate).getTime() : Number.POSITIVE_INFINITY;
          const bTime = b.startDate ? new Date(b.startDate).getTime() : Number.POSITIVE_INFINITY;
          return (aTime - bTime) * direction;
        });

        // Paginate
        const sliceTotal = result.length;
        const offset = params.offset || 0;
        const limit = params.limit || 25;
        result = result.slice(offset, offset + limit);

        if (!cancelled) {
          setData(result);
          setTotal(sliceTotal);
        }
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    params.enabled,
    params.search,
    params.type,
    params.status,
    params.brand,
    params.subtype,
    params.subtypeCountry,
    params.offset,
    params.limit,
    params.startDate,
    params.endDate,
    params.createdBys?.join(','),
    // Joined, like createdBys: a fresh array identity on every render would refetch endlessly,
    // and leaving it out entirely meant switching source segment never refetched at all.
    params.creationSources?.join(','),
    params.sort,
    refreshKey,
  ]);

  return { data, total, loading, error, refetch };
}

// ─── Detail hook ─────────────────────────────────────────────

interface UseCampaignDetailResult {
  data: DetailOpportunity | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetch one campaign's full detail. An empty `id` keeps the hook inert —
 *  callers that resolve their record from somewhere else (e.g. the
 *  guided-tour sample fallback) pass '' rather than branching on the hook. */
/** Optional hints the list already knows, so the detail page can skip the type-discovery search and
 *  fetch the baseline in parallel with the detail instead of after it. */
export interface CampaignDetailHint {
  type?: 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER' | string | null;
  merchantId?: string | null;
  country?: string | null;
}

export function useCampaignDetail(id: string, hint: CampaignDetailHint = {}): UseCampaignDetailResult {
  const [data, setData] = useState<DetailOpportunity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (USE_REAL_API) {
          try {
            // Step 1: resolve the item type. The list row already knows it (passed as a hint), which
            // saves a ~2s search over the whole campaign ledger; fall back to the search when absent.
            const knownType: ApiCampaignType | null =
              hint.type === 'UPSIZE' || hint.type === 'AD_SALES' || hint.type === 'STANDALONE_OFFER'
                ? mapFrontendTypeToApi(hint.type)
                : null;
            let item: { id: string; type: ApiCampaignType };
            if (knownType) {
              item = { id, type: knownType };
            } else {
              const listResponse = await campaignManagerAPI.getItems({ q: id, limit: 1 });
              const found = listResponse.data?.[0];
              if (!found) {
                if (!cancelled) setError('Campaign not found');
                return;
              }
              item = { id: found.id, type: found.type as ApiCampaignType };
            }
            // Baseline only depends on merchant + country; when the row supplied them, fetch it alongside
            // the detail instead of after it.
            const earlyBaseline =
              item.type === 'UPSIZE' && hint.merchantId && hint.country
                ? campaignManagerAPI.getBaseline(hint.merchantId, hint.country).catch(() => null)
                : null;

            // Step 2: Fetch full detail via type-specific GET-by-id
            let detail: unknown;
            if (item.type === 'AD_SALES') {
              detail = await campaignManagerAPI.getOpportunityGroupById(item.id);
            } else if (item.type === 'UPSIZE') {
              detail = await campaignManagerAPI.getOpportunityById(item.id);
            } else {
              detail = await campaignManagerAPI.getDealAssetById(item.id);
            }

            if (!cancelled) {
              const rawDetail = detail as Record<string, unknown>;

              // Render the campaign as soon as its own payload is here; the baseline only feeds the
              // "Baseline" comparison column, so it is merged in when it arrives instead of holding the page.
              setData(mapApiDetailToOpportunity(item.type, rawDetail));

              if (item.type === 'UPSIZE') {
                const merchantId = rawDetail.merchantId as string | undefined;
                const country = rawDetail.country as string | undefined;
                if (merchantId && country) {
                  try {
                    // The early fetch was keyed off the row hints in the URL. Use it only when the
                    // campaign's own payload agrees; a stale or hand-edited link would otherwise
                    // show another market's baseline as the "before" rate on a rate change.
                    const hintMatchesDetail = hint.merchantId === merchantId && hint.country === country;
                    const baseline =
                      earlyBaseline && hintMatchesDetail
                        ? await earlyBaseline
                        : await campaignManagerAPI.getBaseline(merchantId, country);
                    if (!cancelled && baseline?.ratsRate) {
                      setData(mapApiDetailToOpportunity(item.type, { ...rawDetail, _baselineRatsRate: baseline.ratsRate }));
                    }
                  } catch {
                    // Baseline is best-effort — missing it doesn't break the detail view
                  }
                }
              }
            }
          } catch {
            if (!cancelled) {
              setError('Failed to load campaign detail');
              message.error('Failed to load campaign detail');
            }
          }
        } else {
          // Mock path — lookup from SEED_OPPS
          const { findOppById } = await import('../../pages/CampaignDetail/opportunity.mock');
          const opp = findOppById(id);
          if (!cancelled) {
            setData(opp || null);
            if (!opp) setError('Campaign not found');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, refreshKey, hint.type, hint.merchantId, hint.country]);

  return { data, loading, error, refetch };
}

// ─── Mutation hooks ──────────────────────────────────────────

interface UseMutationResult {
  execute: (...args: any[]) => Promise<boolean>;
  loading: boolean;
}

/** 4xx responses carry a human-readable reason from the backend; 5xx and network failures do not. */
const isClientRejection = (err: unknown): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status = Number((err as any)?.response?.status);
  return status >= 400 && status < 500;
};

/** Update campaign status (Approve, Reject, Cancel, Terminate) */
export function useUpdateCampaignStatus(): UseMutationResult {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (
      type: 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER',
      id: string,
      newStatus: string,
      comment?: string,
      payment?: ApiPaymentDto
    ): Promise<boolean> => {
      setLoading(true);
      try {
        if (USE_REAL_API) {
          if (type === 'AD_SALES') {
            await campaignManagerAPI.updateOpportunityGroup(id, {
              group: {
                status: toApiOpportunityStatus(newStatus as OpportunityStatus),
                ...(comment ? { merchantComment: comment } : {}),
              },
              // The payment must be echoed whole (the backend replaces it), but not verbatim —
              // see stripServerOwnedPaymentFields.
              ...(payment && { payment: stripServerOwnedPaymentFields(payment) }),
            });
          } else if (type === 'UPSIZE') {
            await campaignManagerAPI.updateOpportunity(id, {
              status: toApiOpportunityStatus(newStatus as OpportunityStatus),
              ...(comment ? { merchantComment: comment } : {}),
            });
          } else {
            await campaignManagerAPI.updateDealAsset(id, {
              assetStatus: toApiDealAssetStatus(newStatus as OpportunityStatus),
              ...(comment ? { merchantComment: comment } : {}),
            });
          }
        }
        // Mock path: status change handled by caller via local state
        return true;
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiError = (err as any)?.response?.data;
        if (apiError?.code === 'INVALID_TRANSITION') {
          message.error(
            `Cannot transition from ${apiError.from || '?'} to ${apiError.to || '?'}. Allowed: ${
              (apiError.allowedTargets || []).join(', ') || 'none'
            }`
          );
        } else if (apiError?.code === 'FORBIDDEN_NOT_OWNED') {
          message.error('You do not have permission to modify this campaign.');
        } else if (isClientRejection(err) && typeof apiError?.message === 'string' && apiError.message.trim()) {
          // The backend explains what blocked the change (e.g. "This insertion order uses an older
          // format..."); a generic toast would send the merchant hunting for a cause that is on screen.
          message.error(apiError.message);
        } else {
          message.error('Failed to update campaign status.');
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { execute, loading };
}

/** Submit a new campaign (from wizard) */
export function useSubmitCampaign(): UseMutationResult {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (
      type: 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
      // UPSIZE-only: standalone-offer (deal-asset) ids the wizard linked to
      // this opportunity. The opportunity-create payload doesn't carry these,
      // so the backend never sees them unless we follow up with explicit
      // POST /deal-assets/:id/opportunities calls — without these the detail
      // page renders no Featured offer row.
      linkedDealAssetIds?: string[]
    ): Promise<boolean> => {
      setLoading(true);
      try {
        if (USE_REAL_API) {
          if (type === 'AD_SALES') {
            await campaignManagerAPI.createOpportunityGroup(data);
          } else if (type === 'UPSIZE') {
            const created = await campaignManagerAPI.createOpportunity(data);
            if (linkedDealAssetIds && linkedDealAssetIds.length > 0 && created?.id) {
              // Local test fixture — synthetic ids never reach the backend.
              // Stash them in localStorage so the detail page can replay the
              // linkage; only forward the real ids to /deal-assets/:id/opportunities.
              const { isMockOfferId, rememberMockLinkage, MOCK_FEATURED_OFFER_ENABLED } = await import(
                '../../pages/CreateUpsize/components/mockFeaturedOffer'
              );
              const mockIds = linkedDealAssetIds.filter(isMockOfferId);
              const realIds = linkedDealAssetIds.filter((id) => !isMockOfferId(id));
              if (mockIds.length > 0) {
                rememberMockLinkage(String(created.id), mockIds);
              }
              if (realIds.length > 0) {
                const newOppId = Number(created.id);
                // Fan out the links in parallel — a single bad asset shouldn't
                // block the rest, but the user does need to know if any failed.
                const results = await Promise.allSettled(
                  realIds.map((assetId) => campaignManagerAPI.linkDealAssetToOpportunities(assetId, [newOppId]))
                );
                const failed = results.filter((r) => r.status === 'rejected').length;
                // While the local test fixture is enabled, the picker also
                // surfaces real API offers we may not actually have permission
                // to link (draft, off-brand, stale). Treat link failures as
                // silent in fixture mode — only warn in clean production runs.
                if (failed > 0 && !MOCK_FEATURED_OFFER_ENABLED) {
                  message.warning(
                    `Upsize created, but ${failed} featured offer link${
                      failed === 1 ? '' : 's'
                    } failed. Re-link from Campaign Manager.`
                  );
                } else if (failed > 0) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    `[mock fixture] ${failed} real-offer link${
                      failed === 1 ? '' : 's'
                    } failed — warning suppressed because MOCK_FEATURED_OFFER_ENABLED is true.`
                  );
                }
              }
            }
          } else {
            // POST /deal-assets — backend links via opportunityId in request body.
            // The mapper (mapOfferFormToRequest) converts linkedOpportunityId → opportunityId.
            await campaignManagerAPI.createDealAsset(data);
          }
        }
        // Mock path: submit handled by caller (toast + navigate)
        return true;
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiMessage = (err as any)?.response?.data?.message;
        message.error(apiMessage || 'Failed to submit campaign.');
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { execute, loading };
}

// ─── Hook 5: Wizard Init Info ───────────────────────────────

interface UseWizardInitInfoResult {
  data: WizardInitInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetch reference data for create wizards (packages, tiers, brands, country config) */
export function useWizardInitInfo(): UseWizardInitInfoResult {
  const [data, setData] = useState<WizardInitInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      if (USE_REAL_API) {
        try {
          const apiModule = await import('../../utils/api');
          const response = await apiModule.default.getMerchantOpportunitiesInitInfo();
          const apiData = response?.data;

          if (!cancelled) {
            setData({
              merchant: apiData?.merchant || null,
              exposurePackages: apiData?.exposurePackages || [],
              baselineTiers: apiData?.baselineTiers || [],
              adPackages: apiData?.adPackages || [],
              countryConfig: apiData?.countryConfig || {},
              blockedDatesByPackage: apiData?.blockedDatesByPackage || {},
              gstByCountry: apiData?.gstByCountry || {},
            });
          }
        } catch {
          if (!cancelled) {
            setError('Failed to load wizard data');
            message.error('Failed to load wizard data');
          }
        }
      } else {
        // Mock path — aggregate from wizard mockData files
        const upsizeMock = await import('../../pages/CreateUpsize/mockData');
        const adSalesMock = await import('../../pages/CreateAdSales/mockData');

        if (!cancelled) {
          setData({
            merchant: upsizeMock.MOCK_MERCHANT,
            exposurePackages: upsizeMock.EXPOSURE_PACKAGES,
            baselineTiers: upsizeMock.BASELINE_TIERS,
            adPackages: adSalesMock.AD_PACKAGES,
            countryConfig: {},
            blockedDatesByPackage: upsizeMock.BLOCKED_DATES_BY_PACKAGE,
            gstByCountry: adSalesMock.AD_GST_BY_COUNTRY,
          });
        }
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { data, loading, error, refetch };
}

// ─── Hook 6: Campaign Selection ─────────────────────────────

interface UseCampaignSelectionResult {
  data: CampaignSelectionItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetch joinable campaigns for the Linked Campaign picker via GET /v2/mission-control/moop/campaigns */
export function useCampaignSelection(params: CampaignSelectionParams = {}): UseCampaignSelectionResult {
  const [data, setData] = useState<CampaignSelectionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      if (USE_REAL_API) {
        try {
          const response = await campaignManagerAPI.getCampaigns({
            countries: params.countries,
            q: params.q,
            page: params.page,
            limit: params.limit ?? 100,
          });
          if (!cancelled) {
            setData(
              (response.data || []).map((c) => ({
                id: c.id,
                name: c.name,
                start: c.start || '',
                end: c.end || '',
              }))
            );
          }
        } catch {
          if (!cancelled) {
            setError('Failed to load campaigns');
            message.error('Failed to load campaigns');
          }
        }
      } else {
        const { MOCK_MERCHANT } = await import('../../pages/CreateUpsize/mockData');
        let campaigns = [...MOCK_MERCHANT.campaigns];
        if (params.q) {
          const q = params.q.toLowerCase();
          campaigns = campaigns.filter((c) => c.name.toLowerCase().includes(q));
        }
        if (!cancelled) setData(campaigns);
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [params.countries, params.q, params.page, params.limit, refreshKey]);

  return { data, loading, error, refetch };
}

// ─── Hook 7: File Upload ────────────────────────────────────

interface UseFileUploadResult {
  upload: (
    file: File,
    context?: { merchantId?: string; country?: string },
    folderName?: string
  ) => Promise<FileUploadResult>;
  uploading: boolean;
}

/** S3 presigned URL upload: get URL → PUT to S3 → save history */
export function useFileUpload(): UseFileUploadResult {
  const [uploading, setUploading] = useState(false);
  const { user } = useContext(AppContext);
  const ctxMerchantId = (
    typeof user === 'object' && user
      ? (user as Record<string, unknown>).merchantId || (user as Record<string, unknown>).id
      : ''
  ) as string;

  const upload = useCallback(
    async (
      file: File,
      context?: { merchantId?: string; country?: string },
      folderName?: string
    ): Promise<FileUploadResult> => {
      setUploading(true);
      const result: FileUploadResult = { uid: `upload-${Date.now()}`, name: file.name, status: 'error' };

      try {
        if (USE_REAL_API) {
          const apiModule = await import('../../utils/api');

          // Step 1: Get presigned URL (response: { uploadUrl, fileKey, ... })
          const urlResponse = await apiModule.default.getFileUploadUrl(file.type, folderName);
          const signedUrl = urlResponse?.uploadUrl || urlResponse?.data?.url;
          const fileKey = urlResponse?.fileKey || urlResponse?.data?.fileKey;
          const fileUrl = urlResponse?.fileUrl || urlResponse?.data?.fileUrl;

          if (!signedUrl || !fileKey) {
            throw new Error('Failed to get upload URL from server');
          }

          // Step 2: PUT to S3
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', signedUrl, true);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.timeout = 30000;
            xhr.onload = () =>
              xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
            xhr.onerror = () => reject(new Error('Upload network error'));
            xhr.ontimeout = () => reject(new Error('Upload timed out'));
            xhr.send(file);
          });

          // Step 3: Save to upload history (same format as UploadAssetModal)
          const historyResponse = await apiModule.default.saveUploadedFileToHistory({
            fileKey,
            contentType: file.type,
            merchantId: context?.merchantId || ctxMerchantId || '',
            country: context?.country || 'SG',
          });

          result.uid = fileKey;
          result.status = 'done';
          result.url = historyResponse?.fileUrl || fileUrl || '';
          result.fileKey = fileKey;
        } else {
          // Mock path — immediate success with blob URL
          result.url = URL.createObjectURL(file);
          result.status = 'done';
        }
      } catch {
        message.error(`Failed to upload ${file.name}`);
        result.status = 'error';
      } finally {
        setUploading(false);
      }

      return result;
    },
    []
  );

  return { upload, uploading };
}

// ─── Hook 8: Save Draft ─────────────────────────────────────

interface UseSaveDraftResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  execute: (type: DraftCampaignType, data: any, existingId?: string) => Promise<{ success: boolean; data?: any }>;
  loading: boolean;
}

/**
 * Save wizard form state as DRAFT (POST new or PATCH existing).
 * Callers must pass mapper-transformed data (not raw wizard state).
 */
export function useSaveDraft(): UseSaveDraftResult {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (type: DraftCampaignType, formData: any, existingId?: string): Promise<{ success: boolean; data?: any }> => {
      setLoading(true);
      try {
        let responseData: unknown;
        if (USE_REAL_API) {
          if (existingId) {
            // PATCH existing draft
            if (type === 'AD_SALES') {
              const res = await campaignManagerAPI.updateOpportunityGroup(existingId, formData);
              responseData = res;
            } else if (type === 'UPSIZE') {
              const res = await campaignManagerAPI.updateOpportunity(existingId, formData);
              responseData = res;
            } else {
              const res = await campaignManagerAPI.updateDealAsset(existingId, formData);
              responseData = res;
            }
          } else {
            // POST new draft — callers pass mapped DTOs with status set to DRAFT
            if (type === 'AD_SALES') {
              const res = await campaignManagerAPI.createOpportunityGroup(formData);
              responseData = res;
            } else if (type === 'UPSIZE') {
              const res = await campaignManagerAPI.createOpportunity(formData);
              responseData = res;
            } else {
              const res = await campaignManagerAPI.createDealAsset(formData);
              responseData = res;
            }
          }
        }
        // Mock path: no-op — caller handles toast + navigation
        return { success: true, data: responseData };
      } catch (error) {
        message.error(errorMessage(error, 'Failed to save draft. Your changes are still in the editor.'));
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { execute, loading };
}

// ─── Hook 9: Duplicate Campaign ─────────────────────────────

interface UseDuplicateCampaignResult {
  data: DuplicateCampaignData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch source campaign for Duplicate, Resubmit, Edit, and Resume flows.
 * When `preserveDates` is true (edit/resume), keeps dates and proof intact.
 * When false (duplicate/resubmit), strips dates/proof/history.
 * Inert when sourceId is null.
 */
export function useDuplicateCampaign(sourceId: string | null, preserveDates = false): UseDuplicateCampaignResult {
  const [data, setData] = useState<DuplicateCampaignData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceId) {
      setData(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        let source: DetailOpportunity | null = null;

        if (USE_REAL_API) {
          // Find item type via list search, then fetch full detail by type
          const listResponse = await campaignManagerAPI.getItems({ q: sourceId, limit: 1 });
          const item = listResponse.data?.[0];
          if (item) {
            let detail: unknown;
            if (item.type === 'AD_SALES') {
              detail = await campaignManagerAPI.getOpportunityGroupById(item.id);
            } else if (item.type === 'UPSIZE') {
              detail = await campaignManagerAPI.getOpportunityById(item.id);
            } else {
              detail = await campaignManagerAPI.getDealAssetById(item.id);
            }
            // Map raw API response → DetailOpportunity so field names match stripping logic
            // (e.g. raw deal-asset has contents[0].title, mapper normalises to source.title)
            source = mapApiDetailToOpportunity(item.type, detail as Record<string, unknown>);
            // Preserve merchantId from raw response for brand resolution (not in mapped shape)
            const rawDetail = detail as Record<string, unknown>;
            const rawMerchant = rawDetail.merchant as Record<string, unknown> | undefined;
            if (rawMerchant?.id) {
              (source as Record<string, unknown>).merchantId = String(rawMerchant.id);
            } else if (rawDetail.merchantId) {
              (source as Record<string, unknown>).merchantId = String(rawDetail.merchantId);
            }
          }
        } else {
          const { findOppById } = await import('../../pages/CampaignDetail/opportunity.mock');
          source = findOppById(sourceId) || null;
        }

        if (!source) {
          if (!cancelled) setError('Source campaign not found');
          return;
        }

        // Strip dates, proof, history — keep configuration fields
        const stripped: Record<string, unknown> = {};
        const feType = source.type as 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER';

        // Resolve brandId: real API detail may include merchantId or brandId directly.
        // Fall back to source.brand (display name) if no structured ID is available.
        const resolvedBrandId = await (async () => {
          const raw = source as Record<string, unknown>;
          // Prefer structured IDs from API response
          if (raw.brandId) return String(raw.brandId);
          if (raw.merchantId) return String(raw.merchantId);
          // Real API path: lookup from brands endpoint
          if (USE_REAL_API) {
            try {
              const brandsResponse = await campaignManagerAPI.getBrands();
              const match = brandsResponse.data.find((b) => b.name === source.brand);
              if (match) return match.id;
            } catch {
              /* fall through */
            }
          } else {
            const { MOCK_MERCHANT } = await import('../../pages/CreateUpsize/mockData');
            const match = MOCK_MERCHANT.brands.find((b) => b.name === source.brand);
            if (match) return match.id;
          }
          return source.brand; // last resort: display name
        })();

        if (feType === 'UPSIZE') {
          stripped.brandId = resolvedBrandId;
          stripped.merchantComment = preserveDates ? source.merchantComment || '' : '';
          stripped.linkCampaignApplyToAll = false;
          if (source.opportunities) {
            stripped.opportunities = (source.opportunities as Record<string, unknown>[]).map(
              (opp: Record<string, unknown>) => ({
                ...opp,
                startDate: preserveDates ? opp.startDate : null,
                endDate: preserveDates ? opp.endDate : null,
                networkProof: preserveDates ? opp.networkProof || [] : [],
                _dateTouched: preserveDates,
                // Migrate old singular → new array shape
                linkedStandaloneOfferIds: Array.isArray(opp.linkedStandaloneOfferIds)
                  ? opp.linkedStandaloneOfferIds
                  : opp.linkedStandaloneOfferId
                  ? [opp.linkedStandaloneOfferId as string]
                  : [],
              })
            );
          } else {
            // API returns flat structure — build wizard opportunity from it
            const start = fromIsoDatetime(
              preserveDates ? (source.startDate as unknown as string) : null,
              source.country
            );
            const end = fromIsoDatetime(preserveDates ? (source.endDate as unknown as string) : null, source.country);
            stripped.opportunities = [
              {
                id: source.id,
                package: (source as Record<string, unknown>).packageTier || null,
                startDate: start.date,
                endDate: end.date,
                // Fall back to a full day only when there is no source instant to read — a
                // duplicate deliberately clears the window and the wizard needs seed values.
                startTime: start.time ?? '00:00',
                endTime: end.time ?? '23:59',
                tiers: (
                  ((source as Record<string, unknown>).commissionTiers as Array<Record<string, unknown>>) || []
                ).map((t) => ({
                  id: String(t.category),
                  category: String(t.category),
                  baseline: 0,
                  unit: t.commissionType === 'percentage' ? '%' : '$',
                  rate: Number(t.commissionRate || 0),
                  _touched: true,
                })),
                linkedStandaloneOfferIds: [],
                campaignId: (source as Record<string, unknown>).campaignId || null,
                currency: 'SGD',
                networkProof: [],
                _dateTouched: preserveDates,
              },
            ];
          }
        } else if (feType === 'AD_SALES') {
          // Delegate all AD_SALES hydration to the shared helper.
          // Pass empty adPackages — catalog lookup is not available here; display
          // fields fall back to raw API values (same behaviour as before the refactor).
          // The wizard component enriches with catalog data after packages load.
          const hydrated = hydrateAdSalesFormData(source, [], { preserveDates });
          Object.assign(stripped, hydrated);
          // Override brandId with the resolved merchant ID (not exposed by DetailOpportunity)
          stripped.brandId = resolvedBrandId;
          // poNumber is not returned by the mapper; preserve on edit/resume
          stripped.poNumber = preserveDates ? (source as Record<string, unknown>).poNumber || '' : '';
        } else {
          // STANDALONE_OFFER
          stripped.brandId = resolvedBrandId;
          stripped.title = source.title || '';
          stripped.description = source.descriptionLong || '';
          stripped.landingUrl = source.landingUrl || '';
          stripped.heroImage = source.image || '';
          stripped.bannerAlt = (source as Record<string, unknown>).bannerAlt || '';
          const offerStart = fromIsoDatetime(
            preserveDates ? (source.startDate as unknown as string) : null,
            source.country
          );
          const offerEnd = fromIsoDatetime(
            preserveDates ? (source.endDate as unknown as string) : null,
            source.country
          );
          stripped.startDate = offerStart.date;
          stripped.endDate = offerEnd.date;
          stripped.startTime = offerStart.time ?? '00:00';
          stripped.endTime = offerEnd.time ?? '23:59';
          stripped.linkedOpportunityId = preserveDates ? source.linkedOpportunityId || null : null;
        }

        if (!cancelled) {
          setData({ type: feType, sourceId, formData: stripped, country: source.country || '' });
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load source campaign');
          message.error('Failed to load source campaign for duplication');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sourceId, preserveDates]);

  return { data, loading, error };
}

/** Fetch baseline rates for a specific merchant + country.
 *  Returns null while loading or when either param is missing. */
export function useBaseline(merchantId?: string, country?: string) {
  const [data, setData] = useState<BaselineV2Response | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!merchantId || !country) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    campaignManagerAPI
      .getBaseline(merchantId, country)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId, country]);

  return { data, loading };
}
